// VerticalStorefront — per-vertical marketing landing that funnels into
// the LOCKED signup at /{path}/signup. Mirrors the locked-signup hero so
// the storefront and the workspace intake feel like the same product SKU.
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogoPill } from "@/components/Logo";
import {
  Check, ArrowRight, Sparkle, CloudSun, Buildings, Package,
  Bug, Snowflake, House, Heart, ShieldCheck, CheckCircle, Play,
  TreeEvergreen, Drop, Lightning, Wrench, PlugsConnected, Broadcast,
} from "@phosphor-icons/react";
import ProductMockup from "@/components/storefront/ProductMockup";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// One config to rule them all. Each vertical inherits look-and-feel from
// the locked signup (same kicker + hero title) so the funnel feels
// continuous when a visitor clicks the CTA.
const STOREFRONTS = {
  pest_control: {
    key: "pest_control", slug: "pest",
    signup_path: "/pest/signup",
    kicker: "PEST CONTROL EDITION",
    icon: Bug,
    accent: "#f97316", accentBg: "bg-orange-500 hover:bg-orange-600",
    accentText: "text-orange-500", accentSoftBg: "bg-orange-50",
    accentSoftText: "text-orange-700",
    hero_pre: "Ops center for pest control crews.",
    hero_title: "ROUTE.\nSPRAY.\nREPORT.",
    hero_sub: "The one screen that carries your route, your pesticide log, and your customer receipts — plus AI that IDs a bug from a photo and rewrites tomorrow's route around it.",
    features: [
      { icon: ShieldCheck, k: "EPA-ready pesticide log",
        p: "Every application logged with product, EPA reg #, target pest, dilution, and geo-timestamped. Print the audit trail state inspectors ask for in one tap." },
      { icon: Bug, k: "Photo-to-Pest ID",
        p: "Aide snaps a bug at the account. Claude Vision identifies species, severity, and recommended treatment — before you even open the truck." },
      { icon: Sparkle, k: "AI Route Optimizer",
        p: "Overdue accounts, weather, drive time, tech skills — Claude re-sequences your route in 30 seconds so you finish an hour earlier every day." },
      { icon: CheckCircle, k: "Branded service-ticket PDFs",
        p: "Auto-generated tickets with your logo, treatment details, next-service date — email or text to the account before the truck leaves the driveway." },
    ],
    proof: [
      "Recurring account cadences (monthly, quarterly, on-demand) with overdue radar",
      "Route Radar shows every account by drive-time distance from the truck",
      "Auto-linked service tickets — every treatment updates the account's history",
    ],
    price_line: "Unlimited techs · Unlimited accounts · $99/mo per shop.",
  },
  hvac: {
    key: "hvac", slug: "hvac",
    signup_path: "/hvac/signup",
    kicker: "HVAC EDITION",
    icon: Snowflake,
    accent: "#0891b2", accentBg: "bg-cyan-600 hover:bg-cyan-700",
    accentText: "text-cyan-600", accentSoftBg: "bg-cyan-50",
    accentSoftText: "text-cyan-700",
    hero_pre: "Ops center for HVAC contractors.",
    hero_title: "DIAGNOSE.\nWARRANTY.\nCLOSE.",
    hero_sub: "Refrigerant log, warranty tracker, nameplate AI diagnostics, and Manual-J load calc — every tool a residential or commercial shop needs, one login.",
    features: [
      { icon: Snowflake, k: "EPA 608 refrigerant log",
        p: "Every add/recover/leak captured with cert #, refrigerant type, and lbs. Running totals per gas + CSV export for the 3-year retention requirement." },
      { icon: Sparkle, k: "AI Diagnose from a nameplate",
        p: "Tech snaps the outdoor unit's rating plate. Claude reads model + serial, pulls make/tonnage/SEER, and surfaces likely faults for the symptoms called in." },
      { icon: ShieldCheck, k: "Warranty & equipment radar",
        p: "Every unit tracked with install date, warranty end, filter size, and last PM. Filter changes and warranty expirations ping the office before the tenant does." },
      { icon: CheckCircle, k: "Load calc + branded service-call PDFs",
        p: "Manual-J popover for quick sizing. Every service call closes with a branded PDF the homeowner can hand to their landlord." },
    ],
    proof: [
      "Refrigerant CSV export sized for EPA Section 608's 3-year retention rule",
      "Warranty tracker flags every unit within 90 days of expiration",
      "Auto-branded service-call PDFs with your logo, your terms, your total",
    ],
    price_line: "Unlimited techs · Unlimited equipment · $99/mo per shop.",
  },
  roofing: {
    key: "roofing", slug: "roofing",
    signup_path: "/roofing/signup",
    kicker: "ROOFING EDITION",
    icon: House,
    accent: "#dc2626", accentBg: "bg-red-600 hover:bg-red-700",
    accentText: "text-red-600", accentSoftBg: "bg-red-50",
    accentSoftText: "text-red-700",
    hero_pre: "Ops center for roofers.",
    hero_title: "STORM.\nESTIMATE.\nCLOSE.",
    hero_sub: "NWS storm alerts flag every homeowner in the impact zone. Claude Vision reads the damage from photos. SMS estimates close before the next crew even leaves the driveway.",
    features: [
      { icon: CloudSun, k: "NWS storm-flagged job radar",
        p: "Every job auto-tags itself with the last severe weather event at that address — hail, wind, tornado. Pitch the ones that just got hit, not the ones that didn't." },
      { icon: Sparkle, k: "AI Damage Diagnose from photos",
        p: "Tech snaps the roof from the ladder. Claude Vision flags hail impact points, wind uplift, missing shingles, granular loss — before you write the estimate." },
      { icon: CheckCircle, k: "SMS estimates that close",
        p: "Text the homeowner a signed estimate they open on their phone. Twilio + Stripe wired — deposit hits your account before your truck starts the next stop." },
      { icon: ShieldCheck, k: "Branded proposal PDFs",
        p: "Every estimate rendered as a branded PDF with your license #, warranty terms, and photos of the exact damage — homeowners sign in one tap." },
    ],
    proof: [
      "Storm auto-link — every property inside the impact polygon flagged automatically",
      "AR-ready measure + area calc coming Q2 — Xactimate-compatible exports v2",
      "OSHA-friendly fall-safety proof: every roof step auto-prompts a harness photo",
    ],
    price_line: "Unlimited crews · Unlimited jobs · $99/mo per shop.",
  },
  home_health: {
    key: "home_health", slug: "home-care",
    signup_path: "/home-care/signup",
    kicker: "HOME CARE EDITION",
    icon: Heart,
    accent: "#c026d3", accentBg: "bg-fuchsia-600 hover:bg-fuchsia-700",
    accentText: "text-fuchsia-600", accentSoftBg: "bg-fuchsia-50",
    accentSoftText: "text-fuchsia-700",
    hero_pre: "Ops center for home care agencies.",
    hero_title: "VISIT.\nDOCUMENT.\nESCALATE.",
    hero_sub: "Vital signs log, AI care advisor, Claude Vision photo journal, and case-manager PDFs your family will actually read — everything an aide needs on one screen.",
    features: [
      { icon: Heart, k: "Vital signs log with auto-flag alerts",
        p: "BP, pulse, O₂, temp, glucose — logged in seconds. Clinical ranges auto-flag hypertensive crisis, hypoxia, fever, and hypoglycemia so nothing slips through." },
      { icon: Sparkle, k: "AI Care Plan Advisor",
        p: "Aide types 'not eating, more confused today'. Claude returns shift priorities, ADL techniques, red-flag list, and clear 9-1-1 vs. nurse-call triggers — never diagnostic." },
      { icon: CheckCircle, k: "Photo journal + Claude Vision",
        p: "Aide snaps a wound, med tray, or fall hazard. Claude categorizes, describes what's visible, and writes the shift note — no more \"was there redness or not?\"." },
      { icon: ShieldCheck, k: "Case-manager-ready PDFs",
        p: "Every client has a branded care summary with vitals trend, flagged entries, and photo journal — the doc case managers and families actually keep on the fridge." },
    ],
    proof: [
      "HIPAA-aware storage (BAA required for live deployment; PHI never sent over unsecured SMS)",
      "Visit cadences (daily, MWF, T-Th, weekly) with overdue-visit radar",
      "AI never diagnoses — always non-clinical, always with a documentation line",
    ],
    price_line: "Unlimited aides · Unlimited clients · $99/mo per agency.",
  },
  landlord: {
    key: "landlord", slug: "landlord",
    signup_path: "/landlord/signup",
    kicker: "LANDLORD AUTOPILOT",
    icon: Buildings,
    accent: "#0284c7", accentBg: "bg-sky-600 hover:bg-sky-700",
    accentText: "text-sky-600", accentSoftBg: "bg-sky-50",
    accentSoftText: "text-sky-700",
    hero_pre: "For small landlords with 1–20 units.",
    hero_title: "COLLECT.\nDISPATCH.\nRELAX.",
    hero_sub: "Fire your PM company. Your renters pay rent via a magic link. AI triages every repair. Your vetted vendors get texted for you — even at 2 a.m. You approve anything over $200.",
    features: [
      { icon: Buildings, k: "Magic-link renter portal",
        p: "Renters open one URL — no app, no password, no login. Pay rent, submit repairs, see history. Works on any phone in one tap." },
      { icon: Sparkle, k: "AI triage + auto-dispatch",
        p: "Renter submits a repair. Claude Haiku classifies severity, matches your vendor book, drafts the SMS, and auto-texts under your $200 cap. Above? You approve with one tap." },
      { icon: CheckCircle, k: "Rent Autopilot (Stripe)",
        p: "ACH + card rent collection through Stripe. Card fee optionally passed to the renter. Rent ledger tracks every payment for your Schedule E." },
      { icon: ShieldCheck, k: "Your 3 trusted vendors",
        p: "You add your plumber, electrician, HVAC guy once. AI matches the right one to every request. Never search your phone contacts at midnight again." },
    ],
    proof: [
      "Renter portal is a magic link — no login screen, no forgot-password loops",
      "AI drafts the vendor SMS in the renter's own words — you just tap Send (or let it auto-send under your cap)",
      "Stripe Checkout for ACH + card — settles to your bank in 3 business days",
      "Emergency detection (gas smell, active flood, no heat < 32°F) never auto-dispatches — always escalates to you",
    ],
    price_line: "For solo landlords. $19/mo per rental · Cancel anytime.",
  },
  landscaping: {
    key: "landscaping", slug: "landscaping",
    signup_path: "/landscaping/signup",
    kicker: "LANDSCAPING & SNOW EDITION",
    icon: TreeEvergreen,
    accent: "#16a34a", accentBg: "bg-emerald-600 hover:bg-emerald-700",
    accentText: "text-emerald-600", accentSoftBg: "bg-emerald-50",
    accentSoftText: "text-emerald-700",
    hero_pre: "Ops center for landscaping + snow crews.",
    hero_title: "ROUTE.\nSALT.\nBILL.",
    hero_sub: "Weather-triggered snow dispatch. AI route optimizer for mow-and-blow days. Salt & fuel bins that scream when they're empty. Seasonal invoices that write themselves.",
    features: [
      { icon: Snowflake, k: "Weather-triggered snow dispatch",
        p: "Every 6 hours we scan the National Weather Service forecast for your snow accounts. Storm keywords in the next 48h trigger a draft crew SMS — you tap Send." },
      { icon: Sparkle, k: "AI Route Optimizer",
        p: "Load today's mow list, tap Optimize. Claude Haiku re-sequences by zip proximity so the truck drives the least — commercial accounts pushed to the 7am slot." },
      { icon: Package, k: "Salt & fuel inventory with low-stock alerts",
        p: "Tons of rock salt, gallons of diesel, yards of mulch — every bin has a low-stock threshold that pings your dashboard before the next storm empties you out." },
      { icon: CheckCircle, k: "Seasonal billing templates",
        p: "Spring cleanup, mid-season fert, fall cleanup, snow pre-pay — one-tap invoices from templates you can override. Draft → Sent → Paid states track your season." },
    ],
    proof: [
      "NWS free forecast — no API key, no rate limits, US-address native",
      "Route Optimizer runs on Claude Haiku (fast + cheap) — sequence in 5 seconds",
      "Inventory low-stock chip on the dashboard so you never run out mid-storm",
      "Every property tagged 'snow_service' auto-appears on the dispatch board",
    ],
    price_line: "Unlimited crews · Unlimited accounts · $99/mo per shop.",
  },
  plumbing: {
    key: "plumbing", slug: "plumbing",
    signup_path: "/plumbing/signup",
    kicker: "PLUMBING EDITION",
    icon: Drop,
    accent: "#2563eb", accentBg: "bg-blue-600 hover:bg-blue-700",
    accentText: "text-blue-600", accentSoftBg: "bg-blue-50",
    accentSoftText: "text-blue-700",
    hero_pre: "Ops center for plumbing crews.",
    hero_title: "DIAGNOSE.\nQUOTE.\nCLOSE.",
    hero_sub: "AI leak diagnostics that turn diagnostic calls into paid calls, work orders that survive an inspector's callback, and truck-stock reorder so you never come back for the wrong PEX size.",
    features: [
      { icon: Sparkle, k: "AI Leak Diagnose",
        p: "Type what you're seeing (or paste a photo URL). Claude Haiku returns likely cause, next steps, parts list, and a customer estimate — 8-second turnaround. Live now." },
      { icon: Package, k: "Truck-stock parts reorder",
        p: "Every used part decrements your truck. Under-threshold parts land in a weekly purchase order to your supplier. Live now." },
      { icon: ShieldCheck, k: "Permit-ready work orders", coming_soon: true,
        p: "Every job PDF carries the material list and the exact state plumbing code snippet the inspector will ask about. Building this next." },
      { icon: CheckCircle, k: "SMS estimates that close on the truck", coming_soon: true,
        p: "Draft in-app → Twilio SMS → customer taps Approve → Stripe charges. On the roadmap." },
    ],
    proof: [
      "AI Leak Diagnose is live — try it from the demo",
      "Truck Parts Reorder is live",
      "SMS estimates + permit-ready WOs are on the roadmap",
      "State code snippet library rolling out per state",
    ],
    price_line: "Unlimited techs · Unlimited jobs · $99/mo per shop.",
  },
  electrical: {
    key: "electrical", slug: "electrical",
    signup_path: "/electrical/signup",
    kicker: "ELECTRICAL EDITION",
    icon: Lightning,
    accent: "#eab308", accentBg: "bg-yellow-500 hover:bg-yellow-600",
    accentText: "text-yellow-600", accentSoftBg: "bg-yellow-50",
    accentSoftText: "text-yellow-700",
    hero_pre: "Ops center for electricians.",
    hero_title: "PANEL.\nPERMIT.\nCLOSE.",
    hero_sub: "Track panel jobs, EV installs, and permit → rough-in → final on one job record. AI-assisted findings on the roadmap.",
    features: [
      { icon: Broadcast, k: "One job record — permit → rough-in → final",
        p: "One customer, one job, three inspections, one PDF. No more 'which folder is the meter photo in' at 4pm on a Friday. Live now (uses Field Radar's job engine)." },
      { icon: CheckCircle, k: "InspectRadar™ inspections",
        p: "Room-by-room walkthrough with photo evidence per finding. Add NEC code tags manually today; auto-tagging coming next." },
      { icon: Sparkle, k: "AI Load-Calc from nameplate photos",
        p: "Snap the panel + every major appliance nameplate → Claude Vision returns an NEC-220-style load calc + recommended service size. Live now." },
      { icon: ShieldCheck, k: "Panel schedule builder + printable label", coming_soon: true,
        p: "Add breakers, mark AFCI/GFCI/dual, print a directory that fits inside the panel door. Building this next." },
    ],
    proof: [
      "Job record + inspections are live",
      "AI Load-Calc is live — try it from the demo",
      "Panel Schedule builder on the roadmap",
      "NEC 2020/2023 snippet library rolling out",
    ],
    price_line: "Unlimited techs · Unlimited jobs · $99/mo per shop.",
  },
  handyman: {
    key: "handyman", slug: "handyman",
    signup_path: "/handyman/signup",
    kicker: "HANDYMAN EDITION",
    icon: Wrench,
    accent: "#9333ea", accentBg: "bg-purple-600 hover:bg-purple-700",
    accentText: "text-purple-600", accentSoftBg: "bg-purple-50",
    accentSoftText: "text-purple-700",
    hero_pre: "Ops center for solo handymen + 2-3 person crews.",
    hero_title: "BOOK.\nFIX.\nGET PAID.",
    hero_sub: "Booking Radar takes the calls while you're on a ladder. AI drafts help you turn photos into estimates. Job photo journal → branded PDF invoice, one tap.",
    features: [
      { icon: CheckCircle, k: "Booking Radar — customers self-book",
        p: "Public /book/your-name page with your live calendar. First-Available team mode for when you hire. Live now." },
      { icon: Sparkle, k: "AI Drafts Queue",
        p: "Type a customer symptom → AI drafts an estimate / SMS / follow-up you can edit and send. Live now." },
      { icon: Package, k: "Job photo journal → PDF invoice",
        p: "Every job builds itself — before / after photos, materials, labor. One tap turns it into a branded PDF. Live now." },
      { icon: ShieldCheck, k: "Stripe tap-to-pay on the truck", coming_soon: true,
        p: "Apple Pay + card, right on the phone. Money hits your bank next day. On the roadmap." },
    ],
    proof: [
      "Booking Radar + First-Available team mode are live",
      "AI Drafts Queue is live",
      "Tap-to-Pay coming — Stripe Terminal SDK integration next",
      "One flat monthly fee — no per-user seat pricing",
    ],
    price_line: "Solo: $19/mo. Team of 2-3: $49/mo. Cancel anytime.",
  },
  vendors: {
    key: "vendors", slug: "vendors",
    signup_path: "/vendors/signup",
    kicker: "MAINTENANCE VENDOR",
    icon: PlugsConnected,
    accent: "#0d9488", accentBg: "bg-teal-600 hover:bg-teal-700",
    accentText: "text-teal-600", accentSoftBg: "bg-teal-50",
    accentSoftText: "text-teal-700",
    hero_pre: "Ops center for vendors who work for property managers.",
    hero_title: "BID.\nWIN.\nBILL.",
    hero_sub: "Bridge Inbox pulls work orders in from every PM you serve — one screen, one queue. Photo replies land back on their portal so you stop drowning in email threads.",
    features: [
      { icon: PlugsConnected, k: "Bridge Inbox — one screen, every PM",
        p: "Route work orders from Buildium / AppFolio / Yardi / Field Radar into one queue. Live now for email-based intake + PM paste-mode." },
      { icon: CheckCircle, k: "Auto-sync completion photos",
        p: "Photos your crew takes on-site auto-post back to the PM via reply-email — no attachments lost. Live now." },
      { icon: Sparkle, k: "AI Bid Drafter", coming_soon: true,
        p: "New WO lands, click Draft → Claude pulls the job type + past won bids → quote in 30 seconds. On the roadmap." },
      { icon: ShieldCheck, k: "Per-PM SLA scorecard", coming_soon: true,
        p: "Win rate, avg time-to-quote, avg time-to-close per PM. Drop the ones that don't pay on time. Building this next." },
    ],
    proof: [
      "Bridge Inbox is live — Buildium / AppFolio / Yardi / Field Radar",
      "Photo reply-sync is live",
      "AI Bid Drafter + SLA scorecard on the roadmap",
      "Up to 8 PM sources included in the base plan",
    ],
    price_line: "Unlimited techs · Up to 8 PM sources · $79/mo per shop.",
  },
};

export default function VerticalStorefront({ storefrontKey = "pest_control" }) {
  const s = STOREFRONTS[storefrontKey] || STOREFRONTS.pest_control;
  const Icon = s.icon;
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [demoLoading, setDemoLoading] = useState(false);
  const launchDemo = async () => {
    setDemoLoading(true);
    try {
      const { data } = await api.post(`/public/demo/login/${s.key}`);
      // Persist token like normal login
      localStorage.setItem("access_token", data.access_token);
      if (setUser && data.user) setUser(data.user);
      // Tell IndustryContext (and any other auth-scoped provider) to refetch
      // — same-tab writes don't trigger the browser's `storage` event.
      window.dispatchEvent(new Event("auth-changed"));
      toast.success("Live demo loaded — every feature works, playground resets every 30 min");
      nav(data.landing || "/admin");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not launch demo"));
    } finally { setDemoLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-white" data-testid={`storefront-${s.key}`}>
      {/* Nav */}
      <nav className="border-b border-white/10 sticky top-0 z-40 backdrop-blur bg-[#0f172a]/85">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="storefront-logo">
            <LogoPill height={30}/>
          </Link>
          <span className="hidden sm:inline-flex ml-3 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.14em] text-white"
                style={{ background: "#14B8A6" }} data-testid="storefront-industry-chip">
            {s.chip || s.kicker.replace(/ EDITION$/i, "")}
          </span>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-[11px] font-black uppercase tracking-widest text-slate-400 hover:text-white" data-testid="storefront-login">Sign in</Link>
            <Link to={s.signup_path}
              data-testid="storefront-cta-nav"
              className={`h-9 px-4 rounded-lg font-black uppercase text-[10px] tracking-widest text-white ${s.accentBg} inline-flex items-center gap-1 shadow`}>
              Start Free <ArrowRight size={12}/>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-16 sm:pt-24 sm:pb-20 grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
        <div className="lg:col-span-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#00C4CC] bg-[#00C4CC]/10 text-[#00C4CC] text-[10px] font-black uppercase tracking-widest mb-5" data-testid="storefront-kicker">
            <Icon weight="fill" size={12}/> {s.kicker}
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight whitespace-pre-line" data-testid="storefront-title">
            {s.hero_title}
          </h1>
          <p className="mt-6 text-lg text-slate-300 font-medium max-w-2xl" data-testid="storefront-sub">{s.hero_sub}</p>
          <div className="mt-8 flex items-center gap-3 flex-wrap">
            <Link to={s.signup_path}
              data-testid="storefront-cta-hero"
              className={`h-14 px-8 rounded-xl font-black uppercase text-sm tracking-widest text-white ${s.accentBg} inline-flex items-center gap-2 shadow-lg`}>
              Start Free — 60 seconds <ArrowRight size={16}/>
            </Link>
            <button type="button" onClick={launchDemo} disabled={demoLoading}
              data-testid="storefront-cta-demo"
              className="h-14 px-6 rounded-xl font-black uppercase text-xs tracking-widest text-white bg-white/10 hover:bg-white/20 border-2 border-white/30 hover:border-white/60 inline-flex items-center gap-2 disabled:opacity-60 transition">
              <Play weight="fill" size={14}/> {demoLoading ? "Loading demo…" : "See It Live"}
            </button>
          </div>
          <div className="text-xs text-slate-500 mt-4">No credit card. Cancel anytime. {s.hero_pre}</div>
        </div>

        {/* Right — live product preview */}
        <div className="lg:col-span-2">
          <ProductMockup storefrontKey={s.key} accent={s.accent}/>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button type="button" onClick={launchDemo} disabled={demoLoading}
              data-testid="storefront-cta-demo-preview"
              className="h-11 rounded-xl font-black uppercase text-xs tracking-widest text-white bg-white/10 hover:bg-white/20 border-2 border-white/30 hover:border-white/60 inline-flex items-center justify-center gap-2 disabled:opacity-60 transition">
              <Play weight="fill" size={13}/> {demoLoading ? "…" : "See It Live"}
            </button>
            <Link to={s.signup_path}
              data-testid="storefront-cta-preview"
              className={`h-11 rounded-xl font-black uppercase text-xs tracking-widest text-white ${s.accentBg} inline-flex items-center justify-center gap-2 shadow`}>
              Claim Yours <ArrowRight size={14}/>
            </Link>
          </div>
          <div className="mt-2 text-[10px] text-slate-400 text-center font-medium">Demo is read-only · Signup live in 60s</div>
        </div>
      </section>

      {/* Feature cards */}
      <section id="features" className="bg-slate-50 text-slate-900">
        <div className="max-w-6xl mx-auto px-6 py-16 sm:py-24">
          <div className={`inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest ${s.accentSoftBg} ${s.accentSoftText} rounded-full px-3 py-1 mb-4`}>
            <Sparkle weight="fill" size={12}/> Everything wired. Nothing to configure.
          </div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight max-w-3xl">
            Built to close the gap between the field and the office.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {s.features.map((f, i) => {
              const FIcon = f.icon;
              return (
                <div key={i} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm relative" data-testid={`storefront-feature-${i}`}>
                  {f.coming_soon && (
                    <span className="absolute top-3 right-3 text-[9px] font-black uppercase tracking-widest bg-amber-100 text-amber-800 px-2 py-0.5 rounded" data-testid={`feature-coming-soon-${i}`}>
                      Coming soon
                    </span>
                  )}
                  <div className={`w-11 h-11 rounded-xl ${s.accentSoftBg} ${s.accentSoftText} flex items-center justify-center mb-4`}>
                    <FIcon weight="fill" size={22}/>
                  </div>
                  <h3 className="font-display font-black text-xl mb-1.5 tracking-tight">{f.k}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.p}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Proof / bullets */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className={`rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-8 sm:p-12 border border-white/10`} data-testid="storefront-proof">
          <div className={`text-[10px] font-black uppercase tracking-widest mb-2`} style={{ color: s.accent }}>What you get</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-8 max-w-2xl">
            Everything a {s.kicker.replace(" EDITION", "").toLowerCase()} shop needs. Nothing you don't.
          </h2>
          <div className="space-y-4">
            {s.proof.map((line, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`storefront-proof-${i}`}>
                <CheckCircle weight="fill" size={22} className="shrink-0 mt-0.5" style={{ color: s.accent }}/>
                <div className="text-slate-100 font-medium">{line}</div>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to={s.signup_path}
              data-testid="storefront-cta-proof"
              className={`h-12 px-6 rounded-xl font-black uppercase text-xs tracking-widest text-white ${s.accentBg} inline-flex items-center gap-2 shadow-lg`}>
              Start Free Now <ArrowRight size={14}/>
            </Link>
            <span className="ml-4 text-xs text-slate-400 font-medium">{s.price_line}</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-8 flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs text-slate-500">© {new Date().getFullYear()} Field Radar · Command center for the field</div>
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <Link to="/pest" className="hover:text-white">Pest</Link>
            <Link to="/hvac" className="hover:text-white">HVAC</Link>
            <Link to="/roofing" className="hover:text-white">Roofing</Link>
            <Link to="/home-care" className="hover:text-white">Home Care</Link>
            <Link to="/landlord" className="hover:text-white">Landlord</Link>
            <Link to="/landscaping" className="hover:text-white">Landscaping</Link>
            <Link to="/plumbing" className="hover:text-white">Plumbing</Link>
            <Link to="/electrical" className="hover:text-white">Electrical</Link>
            <Link to="/handyman" className="hover:text-white">Handyman</Link>
            <Link to="/vendors" className="hover:text-white">Vendors</Link>
            <Link to="/login" className="hover:text-white">Sign in</Link>
            <Link to={s.signup_path} className="hover:text-white">Start free</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
